import { useState } from 'react';
import { Loader2, Plus, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import InlinePole from './InlinePole';
import { OK, BAD, WARN } from '../../lib/uiTokens';

const fmt = (v) => (v == null ? '—'
    : new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v));

export const REZIM_DPH_LABEL = {
    tuzemsko: 'Tuzemsko',
    reverse_charge: 'Přenesená DP',
    eu_zbozi: 'Pořízení zboží z EU',
    eu_sluzba: 'Pořízení služby z EU',
    dovoz: 'Dovoz (mimo EU)',
    nedanovy: 'Nedaňový (neplátce)',
};

export const TYP_DOKLADU_LABEL = {
    faktura: 'Faktura',
    zjednoduseny: 'Zjedn. daňový',
    paragon: 'Paragon',
    dobropis: 'Dobropis',
};

const SAZBY = [
    { klic: '21', sazba: '21 %', zaklad: 'zaklad_21', dph: 'dph_21' },
    { klic: '12', sazba: '12 %', zaklad: 'zaklad_12', dph: 'dph_12' },
    { klic: '0', sazba: '0 %', zaklad: 'zaklad_0', dph: null },
];

export const maSazbu = (doklad, r) => Math.abs(Number(doklad[r.zaklad]) || 0) > 0.005
    || (r.dph ? Math.abs(Number(doklad[r.dph]) || 0) > 0.005 : false);

/** Sedí rozpad DPH s celkovou částkou dokladu? (tolerance 1 Kč jako na serveru) */
export const rozpadSedi = (doklad) => {
    const celkem = SAZBY.reduce((s, r) => s
        + (Number(doklad[r.zaklad]) || 0)
        + (r.dph ? (Number(doklad[r.dph]) || 0) : 0), 0);
    if (Math.abs(celkem) < 0.005) return null;      // rozpad není — není co porovnávat
    return Math.abs(celkem - (Number(doklad.castka_s_dph) || 0)) <= 1;
};

/** Souhrn rozpadu do jedné pilulky v hlavičce: „12 % · 40,89 + 4,91". */
export const rozpadSouhrn = (doklad) => SAZBY.filter(r => maSazbu(doklad, r)).map(r => ({
    klic: r.klic,
    sazba: r.sazba,
    zaklad: Number(doklad[r.zaklad]) || 0,
    dph: r.dph ? (Number(doklad[r.dph]) || 0) : 0,
}));

/** Řádek páru: popisek vlevo, hodnota na SPOLEČNÉ pravé hraně. */
function Radek({ label, hranice, children }) {
    return (
        <div className="flex items-center gap-2" style={{ minHeight: 32, borderBottom: `1px solid ${hranice}` }}>
            <span className="text-[11.5px] flex-1 min-w-0 truncate" style={{ color: 'var(--text-tertiary)' }}>{label}</span>
            <span className="flex-shrink-0 text-right">{children}</span>
        </div>
    );
}

/**
 * Zaúčtování dokladu — třetí sloupec.
 *
 * Norma chce ve třetím sloupci to, co k položce jen **informuje**. Období, typ
 * dokladu, režim DPH a měna se zadají jednou a pak se na ně jen kouká, takže
 * sem patří — v prostředním sloupci stály jako sekce „Účtování a DPH" a braly
 * místo práci s položkami.
 *
 * Rytmus je jeden sloupec párů: každý řádek 32 px, popisek vlevo, hodnota na
 * jedné pravé hraně, oddělené vlásovou linkou. Rozpad DPH je tabulka
 * sazba · základ · daň — v širokém prostředním sloupci nefungovala, protože
 * roztažená na `1fr` nechala hodnotu plavat 400 px od popisku, ale ve 320 px
 * se sloupce nemají kam roztáhnout a čtyři čísla v tabulce se čtou líp než
 * dvanáct řádků pod sebou.
 */
export default function DokladZauctovaniPanel({
    doklad, isAdmin = false, isDark = false, mutedColor, cardStyle, kompaktni = false, onSave, onZmena,
}) {
    const [pridane, setPridane] = useState(() => new Set());
    const [prepocitavam, setPrepocitavam] = useState(false);

    if (!doklad) return null;

    const hranice = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const videt = SAZBY.filter(r => maSazbu(doklad, r) || pridane.has(r.klic));
    const chybejici = SAZBY.filter(r => !maSazbu(doklad, r) && !pridane.has(r.klic));
    const sedi = rozpadSedi(doklad);

    const jeVychozi = {
        rezim: !doklad.dph_rezim || doklad.dph_rezim === 'tuzemsko',
        mena: !doklad.mena || doklad.mena === 'CZK',
        zaokrouhleni: !doklad.zaokrouhleni || Math.abs(Number(doklad.zaokrouhleni)) < 0.005,
    };

    const cislo = (pole, sirka = 96) => (
        <InlinePole inline typ="number" hodnota={doklad[pole] ?? ''}
            zobrazeni={doklad[pole] == null || Number(doklad[pole]) === 0 ? '—' : fmt(doklad[pole])}
            placeholder="—" editovatelne={isAdmin} isDark={isDark} sirkaInputu={sirka} align="right"
            styl={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}
            onSave={(v) => onSave?.({ [pole]: v })} />
    );

    // Tabulka rozpadu: první sloupec úzký a pevný, dva číselné zarovnané vpravo.
    // Ve 320px sloupci se nemají kam roztáhnout, takže číslo stojí u svého
    // popisku — přesně to, co téhle tabulce nešlo v širokém prostředním sloupci.
    const bunky = { display: 'grid', gridTemplateColumns: '42px 1fr 1fr', gap: 6, alignItems: 'center', padding: '0 8px' };
    // Plocha hlavicky a paticky tabulky - zavoj, ne barva (standard kokpitu).
    const zavoj = isDark ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.035)';
    const soucetZaklad = SAZBY.reduce((a, r) => a + (Number(doklad[r.zaklad]) || 0), 0);
    const soucetDan = SAZBY.reduce((a, r) => a + (r.dph ? (Number(doklad[r.dph]) || 0) : 0), 0);

    const prepocitatKurzem = async () => {
        setPrepocitavam(true);
        try {
            const r = await fetch(`/api/finance/doklady/${doklad.id}/prepocet-kurz`, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content },
            });
            if (r.ok) onZmena?.();
            else { const e = await r.json().catch(() => ({})); alert(e.message || 'Přepočet selhal'); }
        } catch { alert('Síťová chyba'); }
        setPrepocitavam(false);
    };

    return (
        <div className={kompaktni ? 'flex flex-col' : 'rounded-xl overflow-hidden flex flex-col'}
            style={kompaktni ? null : cardStyle}>

            <div className="flex items-center justify-between flex-shrink-0"
                style={{ padding: '9px 12px', borderBottom: `1px solid ${hranice}` }}>
                <span className="text-[10.5px] font-bold uppercase tracking-wide" style={{ color: mutedColor }}>Zaúčtování</span>
                {sedi === false && (
                    <span className="text-[10.5px] font-semibold" style={{ color: BAD }}>rozpad nesedí</span>
                )}
            </div>

            <div style={{ padding: '2px 12px 10px' }}>
                {/* ── BLOK 1: rozpad DPH ──────────────────────────────────────
                    Tabulka má vlastní rámeček a vlastní plochu hlavičky. Dřív
                    jely její řádky na stejné vlásové lince jako páry pod ní,
                    takže se celý panel četl jako jeden nerozlišený seznam —
                    nebylo poznat, kde tabulka končí a kde začínají obecné údaje. */}
                <div className="text-[10px] font-bold uppercase tracking-wide"
                    style={{ color: 'var(--text-tertiary)', margin: '8px 0 5px' }}>Rozpad DPH</div>

                {videt.length === 0 ? (
                    <div className="text-[12px] rounded-lg"
                        style={{ color: 'var(--text-tertiary)', padding: '10px 8px', border: `1px solid ${hranice}` }}>
                        Doklad rozpad DPH nemá.
                    </div>
                ) : (
                    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${hranice}` }}>
                        <div style={{ ...bunky, minHeight: 24, background: zavoj }}>
                            <span className="text-[9.5px] font-bold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>Sazba</span>
                            <span className="text-[9.5px] font-bold uppercase tracking-wide text-right" style={{ color: 'var(--text-tertiary)' }}>Základ</span>
                            <span className="text-[9.5px] font-bold uppercase tracking-wide text-right" style={{ color: 'var(--text-tertiary)' }}>Daň</span>
                        </div>
                        {videt.map(r => (
                            <div key={r.klic} style={{ ...bunky, minHeight: 30, borderTop: `1px solid ${hranice}` }}>
                                <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{r.sazba}</span>
                                <span className="text-right">{cislo(r.zaklad, 84)}</span>
                                <span className="text-right">
                                    {r.dph
                                        ? cislo(r.dph, 84)
                                        : <span className="text-[12.5px]" style={{ color: 'var(--text-tertiary)' }}>&mdash;</span>}
                                </span>
                            </div>
                        ))}
                        {videt.length > 1 && (
                            <div style={{ ...bunky, minHeight: 28, borderTop: `1px solid ${hranice}`, background: zavoj }}>
                                <span className="text-[12px] font-bold" style={{ color: 'var(--text-tertiary)' }}>&Sigma;</span>
                                <span className="text-[12.5px] font-bold text-right tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmt(soucetZaklad)}</span>
                                <span className="text-[12.5px] font-bold text-right tabular-nums" style={{ color: 'var(--text-primary)' }}>{fmt(soucetDan)}</span>
                            </div>
                        )}
                        {/* Verdikt patří dovnitř tabulky — mluví o jejím součtu. */}
                        {sedi != null && (
                            <div className="flex items-center" style={{ minHeight: 24, padding: '0 8px', borderTop: `1px solid ${hranice}`, background: zavoj }}>
                                {sedi ? (
                                    <span className="inline-flex items-center gap-1 text-[10.5px]" style={{ color: OK }}>
                                        <Check size={12} strokeWidth={2.6} /> sedí s celkovou částkou
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold" style={{ color: BAD }}>
                                        <AlertTriangle size={11} /> rozdíl {fmt(Math.abs(soucetZaklad + soucetDan - (Number(doklad.castka_s_dph) || 0)))} Kč proti částce
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ── BLOK 2: doplnění sazby ──────────────────────────────────
                    Volně pod tabulkou, bez linky — je to akce NAD tabulkou,
                    ne další její řádek. */}
                {isAdmin && chybejici.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap" style={{ marginTop: 7 }}>
                        <span className="text-[10.5px]" style={{ color: 'var(--text-tertiary)' }}>Doplnit sazbu:</span>
                        {chybejici.map(r => (
                            <button key={r.klic} type="button" onClick={() => setPridane(p => new Set(p).add(r.klic))}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-semibold"
                                style={{ color: 'var(--text-secondary)', border: `1px solid ${hranice}`, background: 'transparent' }}>
                                <Plus size={10} /> {r.sazba}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── BLOK 3: obecné údaje o zaúčtování ───────────────────────
                    Oddělené linkou přes celou šířku a vlastním nadpisem. */}
                <div className="text-[10px] font-bold uppercase tracking-wide"
                    style={{ color: 'var(--text-tertiary)', marginTop: 14, paddingTop: 10, marginBottom: 2, borderTop: `1px solid ${hranice}` }}>
                    Doklad
                </div>


                <Radek label="Účetní období" hranice={hranice}>
                    <span className="text-[12.5px] tabular-nums" style={{ color: 'var(--text-primary)' }}>
                        {doklad.rok && doklad.mesic ? `${doklad.mesic} / ${doklad.rok}` : '—'}
                    </span>
                </Radek>
                <Radek label="Typ dokladu" hranice={hranice}>
                    <span className="text-[12.5px]" style={{ color: 'var(--text-primary)' }}>
                        {TYP_DOKLADU_LABEL[doklad.typ_dokladu] || doklad.typ_dokladu || '—'}
                    </span>
                </Radek>
                {!jeVychozi.rezim && (
                    <Radek label="Režim DPH" hranice={hranice}>
                        <span className="text-[12.5px] font-semibold" style={{ color: WARN }}>
                            {REZIM_DPH_LABEL[doklad.dph_rezim] || doklad.dph_rezim}
                        </span>
                    </Radek>
                )}
                {!jeVychozi.mena && (
                    <>
                        <Radek label={`Částka v ${doklad.mena}`} hranice={hranice}>{cislo('castka_v_mene')}</Radek>
                        <Radek label="Kurz (Kč/1)" hranice={hranice}>
                            <InlinePole inline typ="number" krok="0.0001" hodnota={doklad.kurz ?? ''}
                                zobrazeni={doklad.kurz ? fmt(doklad.kurz) : 'chybí'} placeholder="chybí"
                                editovatelne={isAdmin} isDark={isDark} sirkaInputu={96}
                                styl={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: doklad.kurz ? 'var(--text-primary)' : WARN }}
                                onSave={(v) => onSave?.({ kurz: v })} />
                        </Radek>
                        {isAdmin && (
                            <div className="flex items-center" style={{ minHeight: 32, borderBottom: `1px solid ${hranice}` }}>
                                <span className="flex-1" />
                                <button type="button" onClick={prepocitatKurzem} disabled={prepocitavam}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold"
                                    style={{ color: 'var(--text-secondary)', border: `1px solid ${hranice}`, background: 'transparent', opacity: prepocitavam ? 0.5 : 1 }}
                                    title="Načíst platný kurz ČNB k DUZP a přepočítat částku i položky na Kč">
                                    {prepocitavam ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                                    Kurz ČNB
                                </button>
                            </div>
                        )}
                    </>
                )}
                {!jeVychozi.zaokrouhleni && (
                    <Radek label="Zaokrouhlení" hranice={hranice}>{cislo('zaokrouhleni')}</Radek>
                )}

                {/* Výchozí hodnoty na jednom tichém řádku — každá stála celý pár. */}
                <div className="flex items-center gap-1.5 flex-wrap" style={{ marginTop: 8 }}>
                    <span className="text-[10.5px]" style={{ color: 'var(--text-tertiary)' }}>Výchozí:</span>
                    {jeVychozi.rezim && <span className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>Tuzemsko</span>}
                    {jeVychozi.rezim && jeVychozi.mena && <span className="text-[10.5px]" style={{ color: 'var(--text-tertiary)' }}>·</span>}
                    {jeVychozi.mena && (
                        <InlinePole inline typ="dropdown" hodnota={doklad.mena || 'CZK'}
                            options={['CZK', 'EUR', 'USD', 'GBP', 'PLN', 'CHF'].map(m => ({ value: m, label: m }))}
                            editovatelne={isAdmin} isDark={isDark} sirkaInputu={88}
                            styl={{ fontSize: 11, color: 'var(--text-secondary)' }}
                            onSave={(v) => onSave?.({ mena: v })} />
                    )}
                    {jeVychozi.zaokrouhleni && <span className="text-[10.5px]" style={{ color: 'var(--text-tertiary)' }}>·</span>}
                    {jeVychozi.zaokrouhleni && (
                        <InlinePole inline typ="number" hodnota={doklad.zaokrouhleni ?? ''}
                            zobrazeni="bez zaokrouhlení" placeholder="bez zaokrouhlení"
                            editovatelne={isAdmin} isDark={isDark} sirkaInputu={96}
                            styl={{ fontSize: 11, color: 'var(--text-secondary)' }}
                            onSave={(v) => onSave?.({ zaokrouhleni: v })} />
                    )}
                </div>

                {/* Co AI vyčetla a sama si tím není jistá. */}
                {doklad.kontrola_ok === false && doklad.kontrola_poznamka && (
                    <div className="flex items-start gap-1.5 rounded-md text-[11px]"
                        style={{ marginTop: 8, padding: 7, background: `${WARN}14`, border: `1px solid ${WARN}4d`, color: 'var(--text-secondary)' }}>
                        <AlertTriangle size={12} style={{ color: WARN, marginTop: 1, flexShrink: 0 }} />
                        <span><b style={{ color: 'var(--text-primary)' }}>K ověření:</b> {doklad.kontrola_poznamka}</span>
                    </div>
                )}

            </div>
        </div>
    );
}
